// COMPANY PEOPLE AND MEMBERSHIPS - super admin.
//
// Owner, 2026-10-15: "I need this to be added in super admin UI/workflow".
// First real use: Sagar Mohora works in Bidlight and needs DP Electric as a
// second company, so he can switch into the DP Electric account and work on
// its two projects. Until now that was a script.
//
// One page per company (from the company table, "People & memberships"):
// everyone whose active company it is, and everyone who has it as a second
// company. From here a super admin can
//   - add an existing person (search -> preview -> confirm),
//   - remove a second company (never the active one - the server says why),
//   - make or remove someone as company admin HERE,
//   - move a person to another company (preview with the projects that hold
//     them -> confirm),
// and undo the last change from the notice it leaves.
//
// EVERY CHANGE IS PREVIEWED BY THE SERVER. The confirm button sends the same
// request again and the server works the plan out afresh; what this page
// shows is never what gets written.
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import {
  Alert, Button, Checkbox, Empty, Input, List, Modal, Select, Space, Spin, Table, Tag, Tooltip, Typography,
} from "antd";
import {
  getCompanyPeople, searchPeople, previewPeopleChange, applyPeopleChange, undoPeopleChange,
  sendPasswordResetLink,
} from "api/adminCompanyPeople";
import { mainAdminGetCompanies } from "api/users/mainAdminGetCompanies";
import { matchOption } from "components/SearchSelect/searchSelect";

const { Text } = Typography;
const errOf = (e, fallback) => (e && (e.err || e.message)) || fallback;

// What the server will do, as it said it: refusals first (they stop the
// confirm), then what changes, then what is worth knowing.
export function PlanView({ plan }) {
  if (!plan) return null;
  return (
    <div data-testid="plan">
      {(plan.blocked || []).map((b) => (
        <Alert key={b.code} type="error" showIcon message={b.message} style={{ marginBottom: 8 }} />
      ))}
      {(plan.changes || []).length ? (
        <>
          <Text strong>What will change</Text>
          <ul style={{ marginTop: 4 }}>
            {plan.changes.map((c) => <li key={c}>{c}</li>)}
          </ul>
        </>
      ) : null}
      {(plan.warnings || []).map((w) => (
        <Alert key={w} type="warning" showIcon message={w} style={{ marginBottom: 8 }} />
      ))}
    </div>
  );
}

function MembershipChips({ person, companyId }) {
  return (
    <Space size={[4, 4]} wrap>
      {(person.memberships || []).map((m) => (
        <Tag key={m.company} color={m.company === companyId ? "blue" : undefined}>
          {m.companyName}
          {m.active ? " · active" : ""}
          {m.companyAdmin ? " · admin" : ""}
          {m.suspended ? " · suspended" : ""}
        </Tag>
      ))}
    </Space>
  );
}

// Preview -> confirm for one change. `request` is { op, body, title }.
function ConfirmChange({ request, onClose, onDone, extra }) {
  const [state, setState] = useState({ loading: false, plan: null, error: "" });
  const [busy, setBusy] = useState(false);

  const look = useCallback(async () => {
    if (!request) return;
    setState({ loading: true, plan: null, error: "" });
    try {
      setState({ loading: false, plan: await previewPeopleChange(request.op, request.body), error: "" });
    } catch (e) {
      setState({ loading: false, plan: null, error: errOf(e, "Could not work that change out") });
    }
  }, [request]);
  useEffect(() => { look(); }, [look]);

  const blocked = !state.plan || (state.plan.blocked || []).length > 0;
  const go = async () => {
    setBusy(true);
    try {
      const out = await applyPeopleChange(request.op, request.body);
      onDone(out);
    } catch (e) {
      setState((s) => ({ ...s, error: errOf(e, "The change was not made") }));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      open={!!request}
      title={request ? request.title : ""}
      onCancel={onClose}
      width={640}
      footer={[
        <Button key="c" onClick={onClose}>Cancel</Button>,
        <Button key="ok" type="primary" disabled={blocked || busy} loading={busy} onClick={go}>Confirm</Button>,
      ]}
    >
      {extra}
      {state.loading ? <Spin /> : null}
      {state.error ? <Alert type="error" showIcon message={state.error} style={{ marginBottom: 8 }} /> : null}
      <PlanView plan={state.plan} />
    </Modal>
  );
}

const PERMISSIONS_NOTE = "An account has one personal permission list and one role. In another company it is the same list, capped by that company's own permission ceiling - there is no separate set of permissions per company.";

function AddPersonDialog({ open, company, onClose, onDone }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState({ loading: false, people: [], note: "", error: "" });
  const [picked, setPicked] = useState(null);
  const [admin, setAdmin] = useState(false);
  const [confirming, setConfirming] = useState(false);

  useEffect(() => {
    if (!open) { setQ(""); setResults({ loading: false, people: [], note: "", error: "" }); setPicked(null); setAdmin(false); setConfirming(false); }
  }, [open]);

  const find = async (text) => {
    setResults({ loading: true, people: [], note: "", error: "" });
    try {
      const out = await searchPeople(text, company.id);
      setResults({ loading: false, people: out.people || [], note: out.note || (out.more ? "More than 20 match - type more of the name or email." : ""), error: "" });
    } catch (e) {
      setResults({ loading: false, people: [], note: "", error: errOf(e, "Search failed") });
    }
  };

  // Memoised: ConfirmChange previews whenever its request changes.
  const request = useMemo(() => (confirming && picked ? {
    op: "add",
    title: `Add ${picked.name} to ${company.name}`,
    body: { userId: picked.id, companyId: company.id, companyAdmin: admin },
  } : null), [confirming, picked, admin, company.id, company.name]);

  return (
    <>
      <Modal
        open={open && !confirming}
        title={`Add an existing person to ${company.name}`}
        onCancel={onClose}
        width={640}
        footer={[
          <Button key="c" onClick={onClose}>Cancel</Button>,
          <Button key="p" type="primary" disabled={!picked || !!picked.here} onClick={() => setConfirming(true)}>Preview</Button>,
        ]}
      >
        <p>
          They keep their own company as the active one; {company.name} becomes a second company they can switch into
          from the company picker. Nobody is emailed.
        </p>
        <Alert type="info" showIcon message="Permissions" description={PERMISSIONS_NOTE} style={{ marginBottom: 12 }} />
        <Input.Search
          placeholder="Email or name"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onSearch={find}
          enterButton="Search"
          aria-label="Search people"
        />
        {results.loading ? <Spin style={{ marginTop: 8 }} /> : null}
        {results.error ? <Alert type="error" showIcon message={results.error} style={{ marginTop: 8 }} /> : null}
        {results.note ? <Text type="secondary">{results.note}</Text> : null}
        <List
          style={{ marginTop: 8 }}
          dataSource={results.people}
          locale={{ emptyText: "No one found yet" }}
          renderItem={(p) => (
            <List.Item
              actions={[
                p.here ? <Tag key="h">already in {company.name}</Tag> : (
                  <Button key="pick" size="small" type={picked && picked.id === p.id ? "primary" : "default"} onClick={() => setPicked(p)}>
                    {picked && picked.id === p.id ? "Chosen" : "Choose"}
                  </Button>
                ),
              ]}
            >
              <List.Item.Meta
                title={<>{p.name} {p.disabled ? <Tag color="red">switched off</Tag> : null}</>}
                description={<>{p.email} · works in {p.activeCompany.name}</>}
              />
            </List.Item>
          )}
        />
        {picked ? (
          <Checkbox checked={admin} onChange={(e) => setAdmin(e.target.checked)} style={{ marginTop: 8 }}>
            Company admin in {company.name}
          </Checkbox>
        ) : null}
      </Modal>
      <ConfirmChange
        request={request}
        onClose={() => setConfirming(false)}
        onDone={(out) => { setConfirming(false); onDone(out); }}
        extra={<Alert type="info" showIcon message={PERMISSIONS_NOTE} style={{ marginBottom: 12 }} />}
      />
    </>
  );
}

function MoveDialog({ person, companies, onClose, onDone }) {
  const [to, setTo] = useState("");
  const [opts, setOpts] = useState({ keepOldMembership: true, companyAdmin: false, overrideHold: false, moveRoster: true, moveAssignments: true });
  const [state, setState] = useState({ loading: false, plan: null, error: "" });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setTo(""); setState({ loading: false, plan: null, error: "" });
    setOpts({ keepOldMembership: true, companyAdmin: false, overrideHold: false, moveRoster: true, moveAssignments: true });
  }, [person]);

  const body = useMemo(() => (person && to ? { userId: person.id, toCompanyId: to, ...opts } : null), [person, to, opts]);
  useEffect(() => {
    let alive = true;
    if (!body) { setState({ loading: false, plan: null, error: "" }); return undefined; }
    setState((s) => ({ ...s, loading: true, error: "" }));
    previewPeopleChange("move", body)
      .then((plan) => { if (alive) setState({ loading: false, plan, error: "" }); })
      .catch((e) => { if (alive) setState({ loading: false, plan: null, error: errOf(e, "Could not work that move out") }); });
    return () => { alive = false; };
  }, [body]);

  const plan = state.plan;
  const heldBlocked = plan && (plan.blocked || []).some((b) => b.code === "held");
  const blocked = !plan || (plan.blocked || []).length > 0;
  const set = (k) => (e) => setOpts((o) => ({ ...o, [k]: e.target.checked }));
  const go = async () => {
    setBusy(true);
    try { onDone(await applyPeopleChange("move", body)); } catch (e) {
      setState((s) => ({ ...s, error: errOf(e, "The move was not made") }));
    } finally { setBusy(false); }
  };
  if (!person) return null;
  const fromName = person.activeCompany.name;
  const targetName = (companies.find((c) => c._id === to) || {}).companyName || "the new company";

  return (
    <Modal
      open={!!person}
      title={`Move ${person.name} to another company`}
      onCancel={onClose}
      width={760}
      footer={[
        <Button key="c" onClick={onClose}>Cancel</Button>,
        <Button key="ok" type="primary" danger={opts.overrideHold} disabled={blocked || busy} loading={busy} onClick={go}>Move</Button>,
      ]}
    >
      <p>
        Re-files the account: their active company becomes the new one, on every project. Today they work
        in <b>{fromName}</b>. Nobody is emailed.
      </p>
      <Select filterOption={matchOption}
        showSearch
        placeholder="Move to..."
        style={{ width: "100%", marginBottom: 12 }}
        value={to || undefined}
        onChange={setTo}
        optionFilterProp="label"
        aria-label="Move to company"
        options={companies
          .filter((c) => c._id !== person.activeCompany.id)
          .map((c) => ({ value: c._id, label: `${c.companyName}${c.enabled === false ? " (suspended)" : ""}` }))}
      />
      <Space direction="vertical" style={{ marginBottom: 12 }}>
        <Checkbox checked={opts.keepOldMembership} onChange={set("keepOldMembership")}>
          Keep {fromName} as a second company (nobody loses anything - they can switch back)
        </Checkbox>
        <Checkbox checked={opts.companyAdmin} onChange={set("companyAdmin")}>
          Company admin in {targetName}
        </Checkbox>
        <Checkbox checked={opts.moveRoster} onChange={set("moveRoster")}>
          Move their partner roster entries to {targetName} where it is on the project
        </Checkbox>
        <Checkbox checked={opts.moveAssignments} disabled={!opts.moveRoster} onChange={set("moveAssignments")}>
          Move the assignments that are theirs alone with them
        </Checkbox>
        {heldBlocked || opts.overrideHold ? (
          <Checkbox checked={opts.overrideHold} onChange={set("overrideHold")}>
            Override: move them anyway, and they lose owner-side access on the projects listed
          </Checkbox>
        ) : null}
      </Space>
      {state.loading ? <Spin /> : null}
      {state.error ? <Alert type="error" showIcon message={state.error} style={{ marginBottom: 8 }} /> : null}
      <PlanView plan={plan} />
      {plan && (plan.held || []).length ? (
        <>
          <Text strong>Projects of {fromName} that hold them</Text>
          <Table
            size="small"
            rowKey="projectId"
            pagination={false}
            dataSource={plan.held}
            style={{ margin: "4px 0 12px" }}
            columns={[
              { title: "Project", dataIndex: "title" },
              { title: "Named as", dataIndex: "why", render: (w) => (w || []).join(", ") },
            ]}
          />
        </>
      ) : null}
      {plan && (plan.roster || []).length ? (
        <>
          <Text strong>Partner roster entries</Text>
          <Table
            size="small"
            rowKey={(r) => r.group || r.owner.id}
            pagination={false}
            dataSource={plan.roster}
            style={{ marginTop: 4 }}
            columns={[
              { title: "Projects of", render: (r) => r.owner.name },
              { title: "Projects", render: (r) => r.projects.map((p) => p.title).join(", ") },
              {
                title: "What happens",
                render: (r) => (r.action === "move"
                  ? <span><Tag color="green">moves</Tag>on these projects only</span>
                  : <span><Tag>stays</Tag>{r.reason}</span>),
              },
            ]}
          />
        </>
      ) : null}
    </Modal>
  );
}

export default function MainAdminPeople() {
  const { idCompany } = useParams();
  const history = useHistory();
  const [data, setData] = useState({ loading: true, error: "", company: null, people: [], staleListed: [], counts: null });
  const [companies, setCompanies] = useState([]);
  const [adding, setAdding] = useState(false);
  const [change, setChange] = useState(null);
  const [moving, setMoving] = useState(null);
  const [notice, setNotice] = useState(null);
  const [undoing, setUndoing] = useState(false);
  // Which person's password link is in flight (their id), so only that row's
  // button is disabled and the rest of the table stays usable.
  const [sendingReset, setSendingReset] = useState("");

  const load = useCallback(async () => {
    setData((d) => ({ ...d, loading: true, error: "" }));
    try {
      const out = await getCompanyPeople(idCompany);
      setData({ loading: false, error: "", ...out });
    } catch (e) {
      setData((d) => ({ ...d, loading: false, error: errOf(e, "Could not load the people of this company") }));
    }
  }, [idCompany]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    mainAdminGetCompanies()
      .then((out) => setCompanies((out && out.company) || []))
      .catch(() => setCompanies([]));
  }, []);

  const company = data.company || { id: idCompany, name: "" };

  const done = (out) => {
    setChange(null); setMoving(null); setAdding(false);
    setNotice({ type: "success", out });
    load();
  };
  const undo = async () => {
    if (!notice || !notice.out || !notice.out.undo) return;
    setUndoing(true);
    try {
      const out = await undoPeopleChange(notice.out.undo);
      setNotice({
        type: out.skipped && out.skipped.length ? "warning" : "info",
        undone: out,
      });
      load();
    } catch (e) {
      setNotice((n) => ({ ...n, undoError: errOf(e, "Undo failed") }));
    } finally { setUndoing(false); }
  };

  // A PASSWORD LINK, ON THE ADMIN'S WORD (owner, 2026-10-26: "add send
  // password reset link to the super admin, members per comany next to make
  // admin button, for users to reset thie password and be able to sign in").
  //
  // This changes NOTHING about the account: the person chooses the password
  // from the link. So it is not a preview -> confirm change like the four
  // around it and leaves no undo - there is nothing to undo. It is confirmed
  // first all the same, because it puts mail in somebody's inbox.
  const sendReset = (p) => {
    Modal.confirm({
      title: `Email ${p.name} a link to ${p.hasPassword ? "reset" : "set"} their password?`,
      content: (
        <div>
          <p style={{ marginBottom: 6 }}>
            It goes to <b>{p.email}</b> and is good for one day. Their current password
            {p.hasPassword ? " keeps working until they choose a new one." : " does not exist yet - this is how they get one."}
          </p>
          <Text type="secondary">Nothing about the account changes until they use the link.</Text>
        </div>
      ),
      okText: "Send the link",
      onOk: async () => {
        setSendingReset(p.id);
        try {
          const out = await sendPasswordResetLink(p.id);
          if (!out || out.err) throw out || {};
          setNotice({
            type: "success",
            sent: { ...out, to: p.email, name: p.name },
          });
        } catch (e) {
          setNotice({ type: "error", sentError: errOf(e, "Could not send that link") });
        } finally { setSendingReset(""); }
      },
    });
  };

  const columns = [
    {
      title: "Person",
      render: (p) => (
        <div>
          <div><b>{p.name}</b> {p.superAdmin ? <Tag color="purple">super admin</Tag> : null}</div>
          <Text type="secondary">{p.email}</Text>
        </div>
      ),
    },
    {
      title: `In ${company.name || "this company"}`,
      render: (p) => (
        <Space size={[4, 4]} wrap>
          {p.here === "active" ? <Tag color="blue">active company</Tag> : <Tag color="gold">second company</Tag>}
          {p.adminHere ? <Tag color="geekblue">company admin</Tag> : null}
        </Space>
      ),
    },
    { title: "All their companies", render: (p) => <MembershipChips person={p} companyId={company.id} /> },
    {
      title: "State",
      render: (p) => (
        <Space size={[4, 4]} wrap>
          {p.disabled ? <Tag color="red">switched off</Tag> : null}
          {p.suspended ? <Tooltip title={`${p.activeCompany.name} is suspended`}><Tag color="orange">suspended</Tag></Tooltip> : null}
          {!p.disabled && !p.suspended ? <Tag color="green">can sign in</Tag> : null}
          {p.hasPassword ? null : (
            <Tooltip title="Only ever signed in by emailed link. A password login answers 'Wrong email or password' for a password that was never set.">
              <Tag color="gold">no password yet</Tag>
            </Tooltip>
          )}
        </Space>
      ),
    },
    {
      title: "Actions",
      render: (p) => (
        <Space size={[4, 4]} wrap>
          <Button
            size="small"
            onClick={() => setChange({
              op: "admin",
              title: p.adminHere ? `Remove ${p.name} as company admin of ${company.name}` : `Make ${p.name} company admin of ${company.name}`,
              body: { userId: p.id, companyId: company.id, companyAdmin: !p.adminHere },
            })}
          >
            {p.adminHere ? "Remove admin here" : "Make admin here"}
          </Button>
          <Tooltip
            title={p.hasPassword
              ? "Email them a link to choose a new password"
              : "This account has never had a password - it has only ever signed in by emailed link. Send them one to set."}
          >
            <Button size="small" loading={sendingReset === p.id} disabled={!!sendingReset} onClick={() => sendReset(p)}>
              {p.hasPassword ? "Send password reset link" : "Send password setup link"}
            </Button>
          </Tooltip>
          {p.here === "second" ? (
            <Button
              size="small"
              onClick={() => setChange({
                op: "remove",
                title: `Remove ${company.name} from ${p.name}'s companies`,
                body: { userId: p.id, companyId: company.id },
              })}
            >
              Remove membership
            </Button>
          ) : (
            <Tooltip title="This is their active company. They must switch to another company first, or use Move.">
              <Button size="small" disabled>Remove membership</Button>
            </Tooltip>
          )}
          <Button size="small" onClick={() => setMoving(p)}>Move to another company</Button>
        </Space>
      ),
    },
  ];

  return (
    <div className="personal-area" style={{ padding: 16 }}>
      <Space style={{ marginBottom: 12 }} wrap>
        <Button onClick={() => history.push("/main-admin")}>Back to companies</Button>
        <Typography.Title level={4} style={{ margin: 0 }}>
          People & memberships{company.name ? ` - ${company.name}` : ""}
        </Typography.Title>
        {data.company && data.company.suspended ? <Tag color="orange">suspended</Tag> : null}
        <Button type="primary" disabled={!data.company} onClick={() => setAdding(true)}>Add existing person</Button>
      </Space>
      {data.counts ? (
        <p>
          {data.counts.total} {data.counts.total === 1 ? "person" : "people"}: {data.counts.active} with {company.name} as their
          active company, {data.counts.second} with it as a second company.
        </p>
      ) : null}

      {notice && notice.out ? (
        <Alert
          type="success"
          showIcon
          closable
          onClose={() => setNotice(null)}
          style={{ marginBottom: 12 }}
          message="Done"
          description={(
            <div>
              <ul style={{ marginBottom: 4 }}>
                {(notice.out.changes || []).map((c) => <li key={c}>{c}</li>)}
              </ul>
              {(notice.out.warnings || []).map((w) => <div key={w}><Text type="warning">{w}</Text></div>)}
              {(notice.out.skipped || []).length ? (
                <div><Text strong>Left as they were: </Text>{notice.out.skipped.join("; ")}</div>
              ) : null}
              {notice.undoError ? <Alert type="error" showIcon message={notice.undoError} style={{ margin: "6px 0" }} /> : null}
              {notice.out.undo ? (
                <Button size="small" onClick={undo} loading={undoing} style={{ marginTop: 6 }}>Undo</Button>
              ) : <Text type="secondary">Undo is not available for this change.</Text>}
            </div>
          )}
        />
      ) : null}
      {notice && (notice.sent || notice.sentError) ? (
        <Alert
          type={notice.sentError ? "error" : "success"}
          showIcon
          closable
          onClose={() => setNotice(null)}
          style={{ marginBottom: 12 }}
          message={notice.sentError ? "The link was not sent" : "Password link sent"}
          description={notice.sentError || (
            <span>
              {notice.sent.kind === "password-setup"
                ? `${notice.sent.name || notice.sent.to} has no password yet, so they were sent a link to SET one`
                : `${notice.sent.name || notice.sent.to} was sent a link to RESET their password`}
              {" "}at <b>{notice.sent.to}</b>. It works for one day. Nothing about the account has changed
              {" "}- their password only changes when they use the link.
            </span>
          )}
        />
      ) : null}
      {notice && notice.undone ? (
        <Alert
          type={notice.type}
          showIcon
          closable
          onClose={() => setNotice(null)}
          style={{ marginBottom: 12 }}
          message="Undone"
          description={(notice.undone.skipped || []).length
            ? `Put back what nobody had changed since. Left as they are now: ${notice.undone.skipped.join("; ")}`
            : "Everything was put back as it was."}
        />
      ) : null}

      {data.error ? <Alert type="error" showIcon message={data.error} style={{ marginBottom: 12 }} /> : null}
      <Table
        rowKey="id"
        loading={data.loading}
        dataSource={data.people}
        columns={columns}
        pagination={{ pageSize: 50, hideOnSinglePage: true }}
        locale={{ emptyText: <Empty description="Nobody works in this company yet" /> }}
        scroll={{ x: true }}
      />
      {(data.staleListed || []).length ? (
        <Alert
          type="info"
          showIcon
          style={{ marginTop: 12 }}
          message={`${data.staleListed.length} more ${data.staleListed.length === 1 ? "account mentions" : "accounts mention"} ${company.name} in an old company list that does not count`}
          description={(
            <div>
              Their active company is not in that list, so it is ignored and they do not work here: {data.staleListed.map((p) => `${p.name} (${p.email}, works in ${p.activeCompany.name})`).join("; ")}.
              To make one of them a member, use Add existing person.
            </div>
          )}
        />
      ) : null}

      <AddPersonDialog open={adding} company={company} onClose={() => setAdding(false)} onDone={done} />
      <ConfirmChange request={change} onClose={() => setChange(null)} onDone={done} />
      <MoveDialog person={moving} companies={companies} onClose={() => setMoving(null)} onDone={done} />
    </div>
  );
}
